import { useEffect, useMemo, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { connectAPI } from '../services/api';
import { Link2, Check, AlertTriangle, Loader, MessageSquare } from 'lucide-react';

// Consent screen for "Connect with AINA Suite".
// A partner sends the merchant here with ?client_id&redirect_uri&webhook_url&state.
export default function ConnectPage() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const params = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return {
      client_id: q.get('client_id') || '',
      redirect_uri: q.get('redirect_uri') || '',
      webhook_url: q.get('webhook_url') || '',
      state: q.get('state') || '',
    };
  }, [location.search]);

  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [channelId, setChannelId] = useState('new');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'merchant') return;
    connectAPI.authorize({ client_id: params.client_id, redirect_uri: params.redirect_uri, webhook_url: params.webhook_url })
      .then(({ data }) => {
        setInfo(data.data);
        // Default to an existing channel not already linked elsewhere, else create a new one.
        const free = data.data.channels.find(c => !c.connectedTo);
        setChannelId(free ? free._id : 'new');
      })
      .catch(err => setError(err.response?.data?.message || 'This connection request is not valid.'));
  }, [user, params]);

  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  const approve = async () => {
    setSubmitting(true);
    try {
      const { data } = await connectAPI.approve({ ...params, channelId });
      window.location.assign(data.data.redirectTo);
    } catch (_) {
      setSubmitting(false); // toast shown by the api interceptor
    }
  };

  // Only reached after /authorize validated redirect_uri against the registered list.
  const deny = () => {
    const url = new URL(params.redirect_uri);
    url.searchParams.set('error', 'access_denied');
    if (params.state) url.searchParams.set('state', params.state);
    window.location.assign(url.toString());
  };

  const selected = info?.channels.find(c => c._id === channelId);
  const webhookHost = info ? safeHost(info.webhookUrl) : '';

  let body;
  if (user.role !== 'merchant') {
    body = (
      <ErrorBox message="Only the business owner account can connect apps. Sign in with the owner account to continue.">
        <button className="btn btn-secondary" style={{ marginTop: 16 }} onClick={logout}>Sign in with a different account</button>
      </ErrorBox>
    );
  } else if (error) {
    body = <ErrorBox message={error} />;
  } else if (!info) {
    body = <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>;
  } else {
    body = (
      <>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginBottom: 20 }}>
          <AppLogo src={info.client.logoUrl} name={info.client.name} />
          <Link2 size={20} color="var(--text-muted)" />
          <img src="/logos.png" alt="AINA Suite" style={{ width: 52, height: 52, objectFit: 'contain' }} />
        </div>

        <h2 style={{ fontSize: '1.15rem', fontWeight: 700, textAlign: 'center', marginBottom: 6 }}>
          {info.client.name} wants to connect to your AINA Suite account
        </h2>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', textAlign: 'center', marginBottom: 20 }}>
          Signed in as <strong>{user.email}</strong>
          {info.client.website && <> · <a href={info.client.website} target="_blank" rel="noreferrer">{safeHost(info.client.website)}</a></>}
        </p>

        <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 8 }}>This app will be able to:</div>
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            'Send your website visitors’ chat messages to your AINA Suite inbox',
            `Receive AI and agent replies at ${webhookHost}`,
          ].map(t => (
            <li key={t} style={{ display: 'flex', gap: 8, fontSize: '0.84rem' }}>
              <Check size={16} color="#22c55e" style={{ flexShrink: 0, marginTop: 1 }} />{t}
            </li>
          ))}
        </ul>

        <div className="form-group">
          <label className="form-label">Web Chat channel to use</label>
          <select className="form-input" value={channelId} onChange={e => setChannelId(e.target.value)}>
            <option value="new">Create a new channel ("{info.client.name} chat")</option>
            {info.channels.map(c => (
              <option key={c._id} value={c._id}>
                {c.name}{c.connectedTo ? ` (connected to ${c.connectedTo})` : ''}
              </option>
            ))}
          </select>
          {selected?.connectedTo && (
            <div className="form-hint" style={{ color: '#d97706', display: 'flex', gap: 6, marginTop: 6 }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              This channel is already connected to {selected.connectedTo}. Continuing will replace that connection.
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button className="btn btn-secondary" style={{ flex: 1, justifyContent: 'center' }} onClick={deny} disabled={submitting}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={approve} disabled={submitting}>
            {submitting && <Loader size={16} style={{ animation: 'spin 0.7s linear infinite' }} />}
            {submitting ? 'Connecting...' : 'Allow'}
          </button>
        </div>
        <p style={{ fontSize: '0.74rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: 14 }}>
          You can disconnect this app any time from Channels in your dashboard.
        </p>
      </>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 20 }}>
      <div className="card" style={{ width: '100%', maxWidth: 460, padding: 28 }}>{body}</div>
    </div>
  );
}

const safeHost = (url) => { try { return new URL(url).host; } catch (_) { return url; } };

function AppLogo({ src, name }) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return <img src={src} alt={name} onError={() => setFailed(true)} style={{ width: 52, height: 52, borderRadius: 12, objectFit: 'cover' }} />;
  }
  return (
    <div style={{ width: 52, height: 52, borderRadius: 12, background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <MessageSquare size={24} />
    </div>
  );
}

function ErrorBox({ message, children }) {
  return (
    <div style={{ textAlign: 'center', padding: '12px 0' }}>
      <AlertTriangle size={36} color="#ef4444" style={{ marginBottom: 12 }} />
      <h2 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: 6 }}>Can't connect this app</h2>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{message}</p>
      {children}
    </div>
  );
}
